import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useFlexLayout, useTable } from 'react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons/faExternalLinkAlt';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import EditSingleToteModal from './edit-single-tote-modal';
import { useExportTotesCSV } from './hooks/use-export-totes-csv';
import { messages } from './messages';
import style from './style.module.less';
import { TotesImportDrawer } from './totes-import/totes-import-drawer';
import { ImportStatus } from '../../../../components/csv-import/data/types/import-status';
import { ImportType } from '../../../../components/csv-import/data/types/import-type';
import { TOTES_CREATE_CLICKED, TOTES_DELETE_CLICKED, TOTES_PRINT_CLICKED } from '../../../../constants/analytics-events';
import { emptyArray } from 'ui-components/default-props/empty';
import { useModal } from '../../../../hooks/use-modal';
import { Segment } from '../../../../services/analytics-services/segment';
import { UserPermissionService } from '../../../../services/user-permission-service';
import Global from '../../../../store/global-selectors';
import { getSellerTotesRequest } from '../../../../store/seller/seller.actions';
import { deleteTotes } from '../../../../store/inventory/inventory.api';
import { Workflows } from '../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../tracking/use-workflow-tracking';
import { generateBarcodeRequest } from '../../../insights/api/reports';
import { ActionButtons } from '../action-buttons';
import DeleteTotesModal from '../modals/delete-totes-modal';

const TOTES_FAQ_LINK = 'https://help.shipstation.com/hc/en-us/articles/38623190668443-Mobile-Picking';

/**
 * Renders the appropriate empty state content based on loading and error states
 * @param {boolean} isLoadingOrFetching - Whether data is currently loading or fetching
 * @param {Error|null} error - Any error that occurred during data fetching
 * @returns {React.ReactElement} The appropriate empty state content
 */
const renderEmptyRow = (isLoadingOrFetching, error) => {
  if (isLoadingOrFetching) {
    return (
      <div className={style['loading-container']}>
        <div className={style['loading-wrapper']}>
          <LoadingIndicator data-testid='loading-indicator' theme='grey' />
        </div>
      </div>
    );
  }

  if (error) {
    return <Text className={style['error-container']} textType={TextType.Paragraph} value={messages.errorLoadingTotes} />;
  }

  return <Text className={style['empty-totes-message']} textType={TextType.Paragraph} value={messages.noTotesMessage} />;
};

/**
 * @param {object} props
 * @param {string|number} props.inventoryWarehouseId - The ID of the inventory warehouse to display totes for
 * @param {() => void} props.onCreateTotes
 * @returns {React.ReactElement} The rendered totes table component
 */

export const TotesTable = ({ inventoryWarehouseId, onCreateTotes }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [totesToDelete, setTotesToDelete] = useState([]);
  const lastSelectedId = useRef(null);

  const dispatch = useDispatch();
  const userRoles = useSelector(Global.selectUseruserRoles) || [];
  const hasToteManagementPermission = UserPermissionService.userHasToteManagementCrudRole(userRoles);
  const exportTotesCSV = useExportTotesCSV(inventoryWarehouseId);

  const allTotes = useSelector(
    (state) => Global.selectSellerTotesByWarehouseId(state, { inventoryWarehouseId: Number(inventoryWarehouseId) }),
    shallowEqual,
  );
  const isLoading = useSelector(Global.selectSellerTotesIsLoading);
  const error = useSelector(Global.selectSellerTotesError);

  const totes = useMemo(() => {
    const totesData = allTotes || emptyArray;
    return totesData.map((tote) => ({
      id: tote.toteId.toString(),
      toteName: tote.toteName,
      barcode: tote.toteBarcode,
    }));
  }, [allTotes]);

  useEffect(() => {
    setSelectedIds([]);
    lastSelectedId.current = null;
  }, [inventoryWarehouseId]);

  const refetchTotes = useCallback(() => {
    dispatch(getSellerTotesRequest());
  }, [dispatch]);

  const allSelected = selectedIds.length === totes.length && totes.length > 0;
  const noneSelected = selectedIds.length === 0;
  const isIndeterminate = !allSelected && !noneSelected;

  const { open: openEditTote, isOpen: isEditToteOpen, close: closeEditTote } = useModal();
  const { open: openImportTotes, isOpen: isImportTotesOpen, close: closeImportTotes } = useModal();
  const [editingTote, setEditingTote] = useState(null);

  const { open: openDeleteModal, isOpen: isDeleteModalOpen, close: closeDeleteModal } = useModal();

  const deleteSelectedTotesMutation = useMutation({
    mutationFn: (/** @type {string[]} */ ids) => {
      Segment.trackEvent(TOTES_DELETE_CLICKED, {
        action: 'delete',
      });

      return deleteTotes(ids).promise;
    },
    onSuccess: () => {
      dispatch(getSellerTotesRequest());
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage {...messages.deleteTotesSuccessTitle} />,
        content: <FormattedMessage {...messages.deleteTotesSuccessContent} />,
        dontHide: true,
      });
    },
    onError: () => {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.deleteTotesFailTitle} />,
        content: <FormattedMessage {...messages.deleteTotesFailContent} />,
        dontHide: true,
      });
    },
    onSettled: () => {
      setSelectedIds([]);
      setTotesToDelete([]);
    },
  });

  const handleConfirmedDelete = useCallback(() => {
    closeDeleteModal();
    deleteSelectedTotesMutation.mutate(totesToDelete);
  }, [deleteSelectedTotesMutation, totesToDelete, closeDeleteModal]);

  const { formatMessage } = useIntl();

  const handleEditTote = useCallback(
    (toteId) => {
      const toteData = totes.find((t) => t.id === toteId);
      setEditingTote(toteData);
      openEditTote();
    },
    [totes, openEditTote],
  );

  const toggleAll = useCallback(() => {
    setSelectedIds((prevSelectedIds) => {
      const currentTotes = totes;
      return currentTotes.length === 0 || prevSelectedIds.length === currentTotes.length ? emptyArray : currentTotes.map((t) => t.id);
    });
    lastSelectedId.current = null;
  }, [totes]);

  const toggleOne = useCallback(
    (id, event) => {
      if (event?.nativeEvent?.shiftKey && lastSelectedId.current !== null) {
        const allIds = totes.map((tote) => tote.id);
        const currentIndex = allIds.indexOf(id);
        const lastIndex = allIds.indexOf(lastSelectedId.current);

        if (currentIndex === -1 || lastIndex === -1) {
          return;
        }

        const startIndex = Math.min(currentIndex, lastIndex);
        const endIndex = Math.max(currentIndex, lastIndex);
        const rangeIds = allIds.slice(startIndex, endIndex + 1);

        setSelectedIds((prev) => {
          const isCurrentlySelected = prev.includes(id);
          if (isCurrentlySelected) {
            const idsToDeselect = new Set(rangeIds);
            return prev.filter((selectedId) => !idsToDeselect.has(selectedId));
          }
          return [...new Set([...prev, ...rangeIds])];
        });
      } else {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]));
      }

      lastSelectedId.current = id;
    },
    [totes],
  );

  const trackWorkflow = useTrackWorkflow(Workflows.CSV_TOTE_IMPORT_FLOW);
  const latestImport = useSelector((state) => Global.selectCsvImportType(state, ImportType.tote));

  const onOpen = () => {
    if (latestImport.status !== ImportStatus.ACTIVE) {
      trackWorkflow(WorkflowSteps.START, { version: 2 });
    }
    openImportTotes();
  };
  const handleBulkDelete = useCallback(() => {
    setTotesToDelete(selectedIds);
    openDeleteModal();
  }, [selectedIds, openDeleteModal]);

  const handleSingleDelete = useCallback(
    (/** @type {string[]} */ idsToDeleteParam) => {
      setTotesToDelete(idsToDeleteParam);
      openDeleteModal();
    },
    [openDeleteModal],
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: 'selection',
        Header: () => (
          <Checkbox
            ariaLabel={formatMessage(messages.selectAllTotes)}
            checked={allSelected}
            indeterminate={isIndeterminate}
            onChange={toggleAll}
          />
        ),
        Cell: ({ row }) => (
          <Checkbox
            checked={selectedIds.includes(row.original.id)}
            data-testid={`checkbox-clickable-${row.values.barcode}`}
            inputId={row.values.barcode}
            onChange={(_checked, event) => toggleOne(row.original.id, event)}
          />
        ),
        disableSortBy: true,
        width: 32,
      },
      {
        Header: <FormattedMessage {...messages.toteNameHeader} />,
        accessor: 'toteName',
        width: 300,
      },
      {
        Header: <FormattedMessage {...messages.barcodeNameHeader} />,
        accessor: 'barcode',
        width: 300,
      },
    ];

    if (hasToteManagementPermission) {
      baseColumns.push({
        id: 'actions',
        Header: <FormattedMessage {...messages.actionsHeader} />,
        Cell: ({ row }) => (
          <ActionButtons onDeleteClick={handleSingleDelete} onEditClick={() => handleEditTote(row.original.id)} toteId={row.original.id} />
        ),
        className: style['row-action-buttons'],
        disableSortBy: true,
        width: 120,
      });
    }

    return baseColumns;
  }, [
    allSelected,
    formatMessage,
    handleSingleDelete,
    handleEditTote,
    hasToteManagementPermission,
    isIndeterminate,
    selectedIds,
    toggleAll,
    toggleOne,
  ]);

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: totes,
    },
    useFlexLayout,
  );

  const printBarcodesMutation = useMutation({
    mutationFn: () => {
      const validTotes = selectedIds.map((id) => totes.find((t) => t.id === id)).filter(Boolean);

      Segment.trackEvent(TOTES_PRINT_CLICKED, {
        warehouseId: inventoryWarehouseId,
        toteCount: validTotes.length,
        toteIds: selectedIds,
      });

      const request = {
        barcodes: validTotes.map((tote) => ({
          name: tote.toteName,
          barcodeContent: tote.barcode,
        })),
      };
      return generateBarcodeRequest(request);
    },
    onSuccess: () => {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage {...messages.printBarcodesSuccessTitle} />,
      });
    },
    onError: () => {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.printBarcodesErrorTitle} />,
        content: <FormattedMessage {...messages.printBarcodesErrorMessage} />,
      });
    },
  });

  return (
    <section>
      <FlexRow className={style['tote-description']} gap={Gap.Space12}>
        <FlexRow className={style['learn-more-wrapper']}>
          <FormattedMessage {...messages.tableDescription} />
          <Button className={style['learn-more-link']} href={TOTES_FAQ_LINK} theme={THEME_CONSTANTS.link}>
            <FormattedMessage
              {...messages.tableLearnMoreLink}
              values={{
                icon: <FontAwesomeIcon className={style['tote-icon']} icon={faExternalLinkAlt} />,
              }}
            />
          </Button>
        </FlexRow>
        <FlexRow className={style['tote-buttons-wrapper']} gap={Gap.Space16}>
          <Button
            disabled={!selectedIds.length || printBarcodesMutation.isPending || deleteSelectedTotesMutation.isPending}
            onClick={printBarcodesMutation.mutate}
            theme={THEME_CONSTANTS.tertiary}
          >
            <FormattedMessage
              {...messages.printTote}
              values={{
                count: selectedIds.length,
              }}
            />
          </Button>
          {hasToteManagementPermission ? (
            <>
              <Button
                disabled={!selectedIds.length || printBarcodesMutation.isPending || deleteSelectedTotesMutation.isPending}
                onClick={handleBulkDelete}
                theme={THEME_CONSTANTS.tertiary}
              >
                <FormattedMessage {...messages.deleteTote} />
              </Button>
              <Button disabled={printBarcodesMutation.isPending} onClick={onOpen} theme={THEME_CONSTANTS.tertiary}>
                <FormattedMessage {...messages.csvImportButton} />
              </Button>
              <Button disabled={printBarcodesMutation.isPending} onClick={exportTotesCSV} theme={THEME_CONSTANTS.tertiary}>
                <FormattedMessage {...messages.csvExportButton} />
              </Button>
              <Button
                onClick={() => {
                  Segment.trackEvent(TOTES_CREATE_CLICKED, {
                    warehouseId: inventoryWarehouseId,
                  });
                  onCreateTotes();
                }}
                theme={THEME_CONSTANTS.primary}
              >
                <FormattedMessage {...messages.createTotes} />
              </Button>
            </>
          ) : null}
        </FlexRow>
      </FlexRow>
      <div className={style['table-container']}>
        <ReactTableWrapper
          emptyRow={renderEmptyRow(isLoading || deleteSelectedTotesMutation.isPending, error)}
          getTableBodyProps={getTableBodyProps}
          getTableProps={getTableProps}
          headerGroups={headerGroups}
          prepareRow={prepareRow}
          rows={rows}
        />
        {isLoading && totes.length > 0 ? (
          <LoadingIndicator className={style['loading-overlay']} data-testid='loading-overlay-indicator' />
        ) : null}
      </div>
      {isEditToteOpen && editingTote ? (
        <EditSingleToteModal
          initialBarcode={editingTote.barcode}
          initialToteName={editingTote.toteName}
          inventoryWarehouseId={inventoryWarehouseId}
          onClose={closeEditTote}
          toteId={editingTote.id}
        />
      ) : null}
      {isImportTotesOpen ? (
        <TotesImportDrawer
          inventoryWarehouseId={inventoryWarehouseId}
          onClose={closeImportTotes}
          onSuccess={refetchTotes}
          trackProcess={trackWorkflow}
        />
      ) : null}
      {isDeleteModalOpen ? <DeleteTotesModal onClose={closeDeleteModal} onDelete={handleConfirmedDelete} /> : null}
    </section>
  );
};
